import { useCallback, useEffect, useMemo, useState } from 'react';
import { DictionaryWord } from 'app/types';
import { STATUS_META, WordStatus, statusOf } from './statusMeta';

export type ViewMode = 'table' | 'cards';
export type StatusFilter = 'all' | WordStatus;
export type CardsPerRow = 1 | 2 | 3;

const VIEW_MODE_KEY = 'dictionary-view-mode';
const CARDS_PER_ROW_KEY = 'dictionary-cards-per-row';

const readViewMode = (): ViewMode => {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    return v === 'cards' || v === 'table' ? v : 'table';
  } catch {
    return 'table';
  }
};

const writeViewMode = (mode: ViewMode) => {
  try {
    localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    /* no-op — quota exhausted or storage disabled */
  }
};

const readCardsPerRow = (): CardsPerRow => {
  try {
    const v = Number(localStorage.getItem(CARDS_PER_ROW_KEY));
    return v === 1 || v === 2 || v === 3 ? (v as CardsPerRow) : 2;
  } catch {
    return 2;
  }
};

const writeCardsPerRow = (n: CardsPerRow) => {
  try {
    localStorage.setItem(CARDS_PER_ROW_KEY, String(n));
  } catch {
    /* no-op */
  }
};

const slivWord = (n: number) => {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return 'слово';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'слова';
  return 'слів';
};

export interface UseWordsViewState {
  search: string;
  setSearch: (v: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (v: StatusFilter) => void;
  hideKnown: boolean;
  setHideKnown: (v: boolean) => void;
  viewMode: ViewMode;
  setViewMode: (m: ViewMode) => void;
  /**
   * Cards-per-row (1 / 2 / 3). Only matters when `viewMode === 'cards'`,
   * but persists across mode switches so the user's choice sticks. Falls
   * back to 1 on narrow viewports — the view layer enforces that, not the
   * hook.
   */
  cardsPerRow: CardsPerRow;
  setCardsPerRow: (n: CardsPerRow) => void;
  filtered: DictionaryWord[];
  selected: Record<number, boolean>;
  toggle: (id: number) => void;
  toggleAll: () => void;
  /**
   * Toggle the same boolean for a batch of ids — flip them all ON if at
   * least one of them is currently off, otherwise flip them all OFF.
   * Used by letter-group headers to select / deselect a whole alphabetical
   * cluster in one tap.
   */
  toggleMany: (ids: number[]) => void;
  clear: () => void;
  setSelectedFromOutside: (next: Record<number, boolean>) => void;
  allSelected: boolean;
  selectedCount: number;
  sliv: (n: number) => string;
  statusFilterOptions: Array<{ value: StatusFilter; label: string }>;
}

const buildStatusOptions = (): Array<{
  value: StatusFilter;
  label: string;
}> => [
  { value: 'all', label: 'Усі' },
  ...(Object.entries(STATUS_META) as Array<[WordStatus, { label: string }]>).map(
    ([key, meta]) => ({ value: key as StatusFilter, label: meta.label }),
  ),
];

const matchesStatus = (
  word: DictionaryWord,
  filter: StatusFilter,
): boolean => filter === 'all' || statusOf(word) === filter;

const matchesSearch = (word: DictionaryWord, search: string): boolean => {
  if (!search) return true;
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    word.word?.toLowerCase().includes(q) ||
    word.translation?.toLowerCase().includes(q) ||
    word.definition?.toLowerCase().includes(q)
  );
};

const isKnown = (word: DictionaryWord) =>
  word.isLearned || word.isActiveSpeech;

export const useWordsView = (
  words: DictionaryWord[],
  resetKey: number,
): UseWordsViewState => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [hideKnown, setHideKnown] = useState(false);
  const [viewMode, setViewModeState] = useState<ViewMode>(() => readViewMode());
  const [cardsPerRow, setCardsPerRowState] = useState<CardsPerRow>(() =>
    readCardsPerRow(),
  );
  const [selected, setSelected] = useState<Record<number, boolean>>({});

  // Reset selection whenever the parent forces it (e.g. after bulk-add).
  useEffect(() => {
    setSelected({});
  }, [resetKey]);

  const setViewMode = useCallback((m: ViewMode) => {
    setViewModeState(m);
    writeViewMode(m);
  }, []);

  const setCardsPerRow = useCallback((n: CardsPerRow) => {
    setCardsPerRowState(n);
    writeCardsPerRow(n);
  }, []);

  const filtered = useMemo(
    () =>
      words.filter((w) => {
        if (!matchesStatus(w, statusFilter)) return false;
        if (hideKnown && isKnown(w)) return false;
        if (!matchesSearch(w, search)) return false;
        return true;
      }),
    [words, statusFilter, hideKnown, search],
  );

  const allSelected =
    filtered.length > 0 && filtered.every((w) => selected[w.id]);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggle = useCallback((id: number) => {
    setSelected((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const all = filtered.every((w) => prev[w.id]);
      if (all) {
        const next = { ...prev };
        filtered.forEach((w) => {
          next[w.id] = false;
        });
        return next;
      }
      const next = { ...prev };
      filtered.forEach((w) => {
        next[w.id] = true;
      });
      return next;
    });
  }, [filtered]);

  const toggleMany = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    setSelected((prev) => {
      const everySelected = ids.every((id) => prev[id]);
      const next = { ...prev };
      ids.forEach((id) => {
        if (everySelected) delete next[id];
        else next[id] = true;
      });
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected({}), []);

  return {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    hideKnown,
    setHideKnown,
    viewMode,
    setViewMode,
    cardsPerRow,
    setCardsPerRow,
    filtered,
    selected,
    toggle,
    toggleAll,
    toggleMany,
    clear,
    setSelectedFromOutside: setSelected,
    allSelected,
    selectedCount,
    sliv: slivWord,
    statusFilterOptions: buildStatusOptions(),
  };
};
